import PropTypes from 'prop-types';
import React from 'react';

/**
 * Components should extend this class if they want to save some or all of their UI settings to local storage.
 * Any component that is re-rendered in a tab cannot store their settings in their state, because that state is shared
 * across all tabs.  Instead, they should extend this class so they can save/retrieve those settings to local storage,
 * which will ensure that all settings will be unique across all tabs.  We are utilizing local storage instead of the
 * redux store because this seems to be the simplest to implement.
 *
 * State still can be used for some UI settings if it is appropriate for them to be shared across all
 */
export default class AbstractLocalStorageComponent extends React.Component {
    static propTypes = {
        id: PropTypes.string // id property serves as the unique key, so must be unique across all tabs!
    };

    /**
     * Can be called by external components to clear the state for a given id
     * @param {string} id 
     */
    static clearState(id) {
        const key = `${id}-state`;
        localStorage.removeItem(key);
    }

    /**
     * Children can override if they don't want to use the id property to define their id
     */
    getStorageId() {
        return this.props.id;
    }

    /**
     * child classes can override if they want to determine the store id using a different method than passing via props
     */
    getLocalStorageKey() {
        return `${this.getStorageId()}-state`;
    }

    getLSState() {
        const key = this.getLocalStorageKey();
        return JSON.parse(localStorage.getItem(key)) || {};
    }

    /**
     * This should be called from a callback every time a UI setting changes.  It will automatically
     * trigger a re-render by setting a bogus state property - to affect the same behavior as if calling setState()
     * @param state
     */
    setLSState(state, options) {
        const storedState = this.getLSState();

        Object.keys(state).forEach((key) => {
            const value = state[key];
            storedState[key] = value;
        });
        const key = this.getLocalStorageKey();
        localStorage.setItem(key, JSON.stringify(storedState));

        if (!options || options.update) {
            this.setState({ refresh: '' }); // trigger an update
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/core/AbstractLocalStorageComponent.js